/**
 * Where an upgrade started (the SketchUp panel links to
 * /for-sketchup2?source=sketchup). The landing remembers it; the checkout
 * sends it to createSubscriptionV4, which stamps it on the Stripe
 * subscription's metadata (functions/stripeSource/checkoutSource.js holds
 * the same allowlist). sessionStorage, not memory: a logged-out visitor
 * round-trips through /start_login_ds before checking out.
 */

export const CHECKOUT_SOURCES = ["sketchup"] as const;
export type CheckoutSource = (typeof CHECKOUT_SOURCES)[number];

const KEY = "checkoutSource";

const isCheckoutSource = (value: unknown): value is CheckoutSource =>
  typeof value === "string" && (CHECKOUT_SOURCES as readonly string[]).includes(value);

/** Remember ?source= from a URL search string, if it is a known source. */
export const rememberCheckoutSource = (search: string) => {
  const source = new URLSearchParams(search).get("source");
  if (!isCheckoutSource(source)) return;
  try {
    sessionStorage.setItem(KEY, source);
  } catch {
    /* private mode / blocked storage: attribution is best effort */
  }
};

/** Read without clearing — a declined card retries with the same source. */
export const peekCheckoutSource = (): CheckoutSource | null => {
  try {
    const value = sessionStorage.getItem(KEY);
    return isCheckoutSource(value) ? value : null;
  } catch {
    return null;
  }
};

/** Clear once the purchase has succeeded, so a later one is not attributed. */
export const clearCheckoutSource = () => {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
};
